import { useTranslationApp } from "../../lib/i18next";
import Loader from "../Loaders/Loader";
import LinearLoaderWithTime from "../Loaders/LinearLoaderWithTime";
import Typography from "@mui/material/Typography";
import {
  EMPTY_RESULTS_SHOW,
  LOADING_INFORMATION,
  PAGE_IN_TABLE,
  RECORDS_IN_TABLE,
} from "../../i18n/keysTranslations";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import Paper from "@mui/material/Paper";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import IconButton from "@mui/material/IconButton";
import TableContainerMUI from "@mui/material/TableContainer";
import { BOX_SHADOW_THEME } from "../../utils/colors";
import { BORDER_RADIUS, IS_ADLE_DOMAIN } from "../../utils/constants";
import { AdleLogoLoader } from "../../assets/svg/AdleLogoLoader";

const TableContainer = ({
  children,
  elevation = 3,
  page,
  onPageChange,
  keys = [],
  rowsPerPage,
  ariaLabel = "",
  hasPagination = true,
  sx,
  additionalValidationNext = false,
  additionalValidationEmpty = false,
  loading = false,
  loadingText = LOADING_INFORMATION,
  emptyText = EMPTY_RESULTS_SHOW,
  isFiltering = false,
  loaderType = "circular",
  loaderTime = 30,
  customComponentBottomLeft = null,
  rounded = true,
  sxContainer = {},
  stickyFooter = null,
  showEmptyText = true,
}) => {
  const { t } = useTranslationApp();

  const getCountRecords = ({ page, rowsPerPage, keys }) => {
    if (keys === 0) return 0;
    var firstRecord = (page - 1) * rowsPerPage + 1;
    var lastRecord = Math.min(page * rowsPerPage, keys);
    return firstRecord + "-" + lastRecord;
  };

  if (loading && loaderType === "circular")
    return (
      <Loader
        size={40}
        fullWidth
        hasMessage={true}
        message={t(loadingText)}
        variantMessage="body1"
      />
    );

  if (loading && loaderType === "linear") {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
        }}
      >
        {IS_ADLE_DOMAIN && <AdleLogoLoader />}
        <LinearLoaderWithTime label={t(loadingText)} time={loaderTime * 1000} />
      </Box>
    );
  }

  if (
    (keys.length === 0 || additionalValidationEmpty) &&
    !loading &&
    showEmptyText
  )
    return (
      <Typography variant="body1" align="center">
        {t(isFiltering ? EMPTY_RESULTS_SHOW : emptyText)}
      </Typography>
    );
  return (
    <Box
      sx={{
        width: "100%",
        overflow: "auto",
        boxShadow: BOX_SHADOW_THEME,
        borderRadius: rounded
          ? `${BORDER_RADIUS * 4}px !important`
          : "0px !important",
        display: "flex",
        flexDirection: "column",
        ...sxContainer,
      }}
    >
      <TableContainerMUI
        component={Paper}
        elevation={elevation}
        sx={{
          backgroundColor: "background.paper",
          borderRadius: rounded
            ? `${BORDER_RADIUS * 4}px !important`
            : "0px !important",
          ...sx,
        }}
      >
        <Table stickyHeader sx={{ minWidth: 450 }} aria-label={ariaLabel}>
          {children}
        </Table>
      </TableContainerMUI>
      {stickyFooter && (
        <Box
          sx={{
            backgroundColor: "background.paper",
            borderTop: "1px solid rgba(224, 224, 224, 1)",
            position: "sticky",
            bottom: 0,
            zIndex: 2,
            overflow: "hidden",
            "& .MuiTable-root": {
              backgroundColor: "background.paper",
            },
          }}
        >
          {stickyFooter}
        </Box>
      )}
      {hasPagination && (
        <Paper
          sx={{
            borderRadius: 0,
            backgroundColor: "background.paper",
            backgroundImage: "none",
            width: "100%",
            display: "flex",
            justifyContent: customComponentBottomLeft
              ? "space-between"
              : "flex-end",
            alignItems: "center",
            gap: 1,
          }}
          elevation={1}
        >
          {customComponentBottomLeft && customComponentBottomLeft}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2">{`${getCountRecords({
              page: page + 1,
              rowsPerPage,
              keys: keys.length,
            })} ${t(RECORDS_IN_TABLE)}`}</Typography>
            <Typography variant="body2">{`${t(PAGE_IN_TABLE)} N°${
              page + 1
            }`}</Typography>
            <Box>
              <IconButton
                disabled={page === 0 || loading}
                onClick={() => onPageChange(page - 1)}
              >
                <NavigateBeforeIcon />
              </IconButton>
              <IconButton
                onClick={() => onPageChange(page + 1)}
                disabled={
                  keys.length <= rowsPerPage * (page + 1) ||
                  additionalValidationNext ||
                  loading
                }
              >
                <NavigateNextIcon />
              </IconButton>
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default TableContainer;
